import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { getCurrentSurvey, submitSurvey } from '../../services/surveyService';
import { hasSubmitted, markSubmitted } from './surveySubmitted';

// TR palette from DESIGN.md; retro tints reuse the status roles (success / error / info).
const C = {
  green: '#1A2E22', greenDark: '#0F1A14', orange: '#D74B00', orangeLight: '#E67234',
  floor: '#F3F5F4', paper: '#FFFFFF', ink: '#1A1A1A', ink2: '#3D4540', ink3: '#4A524D',
  line: '#E3E8E5', hair: '#ECEFED', field: '#CBD3CE', chip: '#E6EFE8', onGreen: '#E4ECE6', onGreenMuted: '#B9CEC0',
};
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const SHADOW = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)';
const FOCUS = { outline: `3px solid ${C.orange}`, outlineOffset: '2px' };

const PlusIcon = () => <path d="M12 5v14M5 12h14" />;
const StopIcon = () => <rect x="6" y="6" width="12" height="12" rx="2" />;
const LoopIcon = () => <path d="M4 12a8 8 0 0 1 14-5.3L20 9M20 4v5h-5M20 12a8 8 0 0 1-14 5.3L4 15M4 20v-5h5" />;
const Svg = ({ children, size = 18, ...props }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>{children}</svg>
);

const RETRO_BOXES = [
  { id: 'start', label: 'Start', hint: 'What should we start doing?', bg: '#E3F1E6', fg: '#1F4D2B', Icon: PlusIcon },
  { id: 'stop', label: 'Stop', hint: 'What should we stop doing?', bg: '#FBE6E4', fg: '#8A1F18', Icon: StopIcon },
  { id: 'continue', label: 'Continue', hint: "What's working that we should keep?", bg: '#E2EDF8', fg: '#174A80', Icon: LoopIcon },
];
const EMPTY_RETRO = { start: '', stop: '', continue: '' };

/** 1–5 buttons for one question; the picked one fills deep green. */
const Rating = ({ question, scale, value, onPick }) => (
  <Box role="group" aria-labelledby={`q-${question.id}`} sx={{ display: 'flex', gap: 0.75, flex: '1 0 auto' }}>
    {scale.map((s) => {
      const picked = value === s.value;
      return (
        <Box
          key={s.value}
          component="button"
          type="button"
          aria-pressed={picked}
          aria-label={`${s.value}: ${s.label}`}
          onClick={() => onPick(s.value)}
          sx={{
            width: { xs: 'auto', sm: 52 }, flex: { xs: 1, sm: 'none' }, minWidth: 44, height: 44,
            borderRadius: '10px', font: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer',
            border: `1.5px solid ${picked ? C.green : '#D5DCD8'}`,
            bgcolor: picked ? C.green : '#F6F8F7', color: picked ? '#FFFFFF' : C.green,
            transition: `background-color 0.2s ${EASE}, color 0.2s ${EASE}, border-color 0.2s ${EASE}`,
            '&:hover': { borderColor: C.green },
            '&:focus-visible': FOCUS,
          }}
        >
          {s.value}
        </Box>
      );
    })}
  </Box>
);

const QuestionRow = ({ question, index, scale, value, onPick }) => {
  const done = value != null;
  return (
    <Box
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 32px', px: 3, py: 2.25,
        borderTop: index > 0 ? `1px solid ${C.hair}` : 'none',
        bgcolor: done ? 'rgba(26,46,34,0.03)' : C.paper,
        transition: `background-color 0.2s ${EASE}`,
      }}
    >
      <Box sx={{ flex: '999 1 420px', minWidth: 0, display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
        <Box
          aria-hidden="true"
          sx={{
            flexShrink: 0, display: 'inline-flex', width: 28, height: 28, borderRadius: '999px', alignItems: 'center', justifyContent: 'center',
            fontSize: 13.5, fontWeight: 600, bgcolor: done ? C.green : C.chip, color: done ? '#FFFFFF' : C.green,
            transition: `background-color 0.2s ${EASE}, color 0.2s ${EASE}`,
          }}
        >
          {done ? '✓' : index + 1}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography id={`q-${question.id}`} component="h3" sx={{ fontSize: 15.5, fontWeight: 600, color: C.green, lineHeight: 1.4 }}>
            {question.title}
          </Typography>
          <Typography sx={{ mt: 0.375, fontSize: 14.5, lineHeight: 1.5, color: C.ink2, maxWidth: '70ch' }}>{question.text}</Typography>
        </Box>
      </Box>
      <Rating question={question} scale={scale} value={value} onPick={onPick} />
    </Box>
  );
};

/** One retro column: tinted header with its icon, then a box that grows to the bottom of the page. */
const RetroColumn = ({ box, value, maxLength, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 240, bgcolor: C.paper, border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px', overflow: 'hidden', boxShadow: SHADOW }}>
    <Box component="label" htmlFor={`retro-${box.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.25, py: 1.75, bgcolor: box.bg, color: box.fg, fontSize: 15, fontWeight: 600 }}>
      <Svg><box.Icon /></Svg>
      {box.label}
    </Box>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: '16px 18px 18px' }}>
      <Box
        component="textarea"
        id={`retro-${box.id}`}
        value={value}
        maxLength={maxLength}
        placeholder={box.hint}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          flex: 1, width: '100%', minHeight: 160, boxSizing: 'border-box', resize: 'none',
          p: '12px 14px', borderRadius: '10px', border: `1.5px solid ${C.field}`, bgcolor: C.paper,
          font: 'inherit', fontSize: 14.5, lineHeight: 1.5, color: C.ink,
          transition: `border-color 0.2s ${EASE}`,
          '&::placeholder': { color: '#6B746E', opacity: 1 },
          '&:focus': { outline: 'none', borderColor: C.green, borderWidth: '2px', p: '11px 13px' },
        }}
      />
      <Typography aria-live="polite" sx={{ mt: 0.75, fontSize: 12.5, color: C.ink3, textAlign: 'right' }}>
        {value.length} / {maxLength}
      </Typography>
    </Box>
  </Box>
);

const ProgressBar = ({ answered, total }) => (
  <Box
    role="progressbar"
    aria-label="Questions answered"
    aria-valuemin={0}
    aria-valuemax={total}
    aria-valuenow={answered}
    sx={{ flex: 1, maxWidth: 360, height: 6, borderRadius: '999px', bgcolor: C.line, overflow: 'hidden' }}
  >
    <Box sx={{ height: '100%', width: `${total ? Math.round((answered / total) * 100) : 0}%`, borderRadius: '999px', bgcolor: C.orange, transition: `width 0.25s ${EASE}` }} />
  </Box>
);

/**
 * Public, anonymous sprint survey for one stream (/survey/:stream).
 * No sign-in; nothing that identifies the person is sent or stored.
 */
const SurveyPage = () => {
  const { stream } = useParams();
  // status: loading | notFound | error | closed | open | done
  const [state, setState] = useState({ status: 'loading', data: null });
  const [answers, setAnswers] = useState({});
  const [retro, setRetro] = useState(EMPTY_RETRO);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null });
    setAnswers({});
    setRetro(EMPTY_RETRO);
    getCurrentSurvey(stream)
      .then((data) => {
        if (cancelled) return;
        if (!data.open) setState({ status: 'closed', data });
        else if (hasSubmitted(data.round.id)) setState({ status: 'done', data });
        else setState({ status: 'open', data });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: err.status === 404 ? 'notFound' : 'error', data: null });
      });
    return () => { cancelled = true; };
  }, [stream]);

  const { status, data } = state;
  const questions = data?.round?.questions || [];
  const scale = data?.scale || [];
  const maxLength = data?.retroMaxLength || 1000;
  const answeredCount = questions.filter((q) => answers[q.id]).length;
  const remaining = questions.length - answeredCount;
  const ready = questions.length > 0 && remaining === 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitSurvey(stream, data.round.id, questions.map((q) => answers[q.id]), retro);
      markSubmitted(data.round.id);
      setState((s) => ({ ...s, status: 'done' }));
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const message = (severity, text) => <Alert severity={severity} sx={{ mx: { xs: 2, sm: 6 }, mt: 4 }}>{text}</Alert>;
  const scaleLegend = scale.length
    ? `1 ${scale[0].label} · 3 ${scale[2]?.label || 'Neutral'} · ${scale.length} ${scale[scale.length - 1].label}`
    : '';

  return (
    // body has zoom: 0.8 (assets/styles/main.css), so 100vh / 0.8 is the full window and Retro reaches the bottom.
    <Box sx={{ minHeight: 'calc(100vh / 0.8)', display: 'flex', flexDirection: 'column', bgcolor: C.floor, color: C.ink }}>
      <Box component="header" sx={{ bgcolor: C.green, color: '#FFFFFF', px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 3.5 } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px 40px', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center', fontSize: 14, fontWeight: 500, color: C.onGreenMuted }}>
              <span>{data?.stream?.name || 'Sprint survey'}</span>
              {data?.round && <><span aria-hidden="true">·</span><Box component="span" sx={{ color: '#FFFFFF' }}>{data.round.sprintName}</Box></>}
            </Box>
            <Typography component="h1" sx={{ mt: 0.75, fontSize: { xs: 28, sm: 36 }, fontWeight: 300, letterSpacing: '-0.02em', lineHeight: 1.2, color: '#FFFFFF' }}>
              Sprint health survey
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, maxWidth: 520, fontSize: 14, lineHeight: 1.5, color: C.onGreen }}>
            <Svg size={20} stroke={C.orangeLight} strokeWidth="2" style={{ flexShrink: 0 }}>
              <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </Svg>
            <span>Anonymous. No sign-in, nothing that identifies you is stored. Results are shared with your team and leadership.</span>
          </Box>
        </Box>
      </Box>

      {status === 'loading' && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress sx={{ color: C.green }} /></Box>}
      {status === 'notFound' && message('warning', 'This survey link is not valid. Check the link you were sent.')}
      {status === 'error' && message('error', 'The survey could not be loaded. Try again in a moment.')}
      {status === 'closed' && message('info', 'No survey is open for this team right now.')}
      {status === 'done' && message('success', 'Thank you. Your response for this sprint has been recorded.')}

      {status === 'open' && (
        <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3.5, px: { xs: 2, sm: 6 }, pt: 4, pb: 4 }}>
            <Box component="section" aria-labelledby="questions-head" sx={{ bgcolor: C.paper, border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px', boxShadow: SHADOW, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.25, bgcolor: '#F8F9FA', borderBottom: `2px solid ${C.line}` }}>
                <Typography id="questions-head" component="h2" sx={{ fontSize: 17, fontWeight: 600, color: C.green }}>How did the sprint go?</Typography>
                <Typography aria-hidden="true" sx={{ fontSize: 12.5, fontWeight: 500, color: C.ink3 }}>{scaleLegend}</Typography>
              </Box>
              {questions.map((q, index) => (
                <QuestionRow
                  key={q.id}
                  question={q}
                  index={index}
                  scale={scale}
                  value={answers[q.id]}
                  onPick={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                />
              ))}
            </Box>

            {/* Retro fills the rest of the page: the section and its columns stretch to the bottom. */}
            <Box component="section" aria-labelledby="retro-head" sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', alignItems: 'baseline' }}>
                <Typography id="retro-head" component="h2" sx={{ fontSize: 20, fontWeight: 500, color: C.green }}>Retro</Typography>
                <Typography sx={{ fontSize: 14, color: C.ink3 }}>
                  Optional. Shown to your team and leadership word for word, so leave out names and details that point to you.
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                {RETRO_BOXES.map((box) => (
                  <RetroColumn key={box.id} box={box} value={retro[box.id]} maxLength={maxLength} onChange={(v) => setRetro((r) => ({ ...r, [box.id]: v }))} />
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ position: 'sticky', bottom: 0, zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: '12px 24px', alignItems: 'center', px: { xs: 2, sm: 6 }, py: 1.75, bgcolor: C.paper, borderTop: `1px solid ${C.line}`, boxShadow: '0 -4px 12px rgba(0,0,0,0.06)' }}>
            <Box sx={{ flex: '1 1 280px', display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <ProgressBar answered={answeredCount} total={questions.length} />
              <Typography sx={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap' }}>{answeredCount} of {questions.length} answered</Typography>
            </Box>
            {submitError
              ? <Alert severity="error" sx={{ py: 0 }}>{submitError}</Alert>
              : <Typography sx={{ fontSize: 13.5, color: C.ink3 }}>{ready ? 'Ready to send. One response per sprint.' : `${remaining} ${remaining === 1 ? 'question' : 'questions'} to go. Retro is optional.`}</Typography>}
            <Box
              component="button"
              type="submit"
              disabled={submitting || !ready}
              sx={{
                height: 48, px: 3.5, borderRadius: '10px', border: 'none', font: 'inherit', fontSize: 15.5, fontWeight: 600,
                bgcolor: C.green, color: '#FFFFFF', cursor: 'pointer', boxShadow: '0 3px 6px rgba(0,0,0,0.12)',
                transition: `background-color 0.2s ${EASE}, transform 0.2s ${EASE}, box-shadow 0.2s ${EASE}`,
                '&:hover:not(:disabled)': { bgcolor: '#2C4A3A', transform: 'translateY(-2px)', boxShadow: '0 8px 10px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.06)' },
                '&:active:not(:disabled)': { bgcolor: C.greenDark, transform: 'none', boxShadow: SHADOW },
                '&:focus-visible': FOCUS,
                '&:disabled': { bgcolor: '#DDE3DF', color: '#59625C', cursor: 'not-allowed', boxShadow: 'none' },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover:not(:disabled)': { transform: 'none' } },
              }}
            >
              {submitting ? 'Submitting…' : 'Submit response'}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SurveyPage;
